import React, { useEffect, useMemo, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

export default function ScopeAssignModal({
  isOpen,
  onClose,
  assessmentId,
  initialDepartments = [],
  onSaved
}) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [selectedDeptIds, setSelectedDeptIds] = useState(() => new Set(initialDepartments.map(d => d.department_id || d.id)));
  const [deptUsers, setDeptUsers] = useState({}); // { [deptId]: [{id,name,email,core_role,app_role}] }
  const [assignments, setAssignments] = useState({}); // { [deptId]: Set<userId> }
  const [surveys, setSurveys] = useState([]); // survey templates
  const [deptSurvey, setDeptSurvey] = useState({}); // { [deptId]: survey_id|null }
  const [deptDueAt, setDeptDueAt] = useState({}); // { [deptId]: ISO string }
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      setLoading(true);
      try {
        // Load survey templates
        try {
          const sres = await fetch(`${API_BASE}/risk-assessment/surveys`, { credentials: 'include' });
          const sdata = await sres.json();
          if (sdata.success) setSurveys(Array.isArray(sdata.surveys) ? sdata.surveys : []);
        } catch (_) {}

        const res = await fetch(`${API_BASE}/departments/with-aliases`, { credentials: 'include' });
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setDepartments(list);
      } catch (_) {}
      setLoading(false);
    })();
  }, [isOpen]);

  useEffect(() => {
    // Prefill assignments map for existing departments
    const map = {};
    const tmpl = {};
    const due = {};
    for (const dept of initialDepartments) {
      const key = dept.department_id || dept.id;
      if (key) {
        map[key] = new Set();
        if (dept.survey_id) tmpl[key] = dept.survey_id;
        if (dept.due_at) due[key] = dept.due_at;
      }
    }
    setAssignments(prev => ({ ...map, ...prev }));
    setDeptSurvey(prev => ({ ...tmpl, ...prev }));
    setDeptDueAt(prev => ({ ...due, ...prev }));
  }, [initialDepartments]);

  const filteredDepartments = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return departments;
    return departments.filter(d => d.name?.toLowerCase().includes(q) || (d.aliases || []).join(',').toLowerCase().includes(q));
  }, [departments, query]);

  const toggleDept = (deptId) => {
    setSelectedDeptIds(prev => {
      const next = new Set(prev);
      if (next.has(deptId)) next.delete(deptId); else next.add(deptId);
      return next;
    });
  };

  const ensureDeptUsersLoaded = async (deptId) => {
    if (deptUsers[deptId]) return;
    try {
      const res = await fetch(`${API_BASE}/users/by-department/${deptId}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setDeptUsers(prev => ({ ...prev, [deptId]: data.users }));
      }
    } catch (_) {}
  };

  const toggleAssignment = (deptId, userId) => {
    setAssignments(prev => {
      const next = { ...prev };
      const setForDept = new Set(next[deptId] || []);
      if (setForDept.has(userId)) setForDept.delete(userId); else setForDept.add(userId);
      next[deptId] = setForDept;
      return next;
    });
  };

  const handleAutoSuggest = async () => {
    // Simple heuristic: prefer users with app_role 'lead', then 'supervisor', then 'admin' (memo roles)
    const next = {};
    for (const deptId of selectedDeptIds) {
      const users = deptUsers[deptId] || [];
      const lead = users.find(u => u.app_role === 'lead');
      const supervisor = users.find(u => u.app_role === 'supervisor');
      const admin = users.find(u => u.app_role === 'admin');
      const chosen = [lead?.id, supervisor?.id, admin?.id].filter(Boolean).slice(0, 2);
      next[deptId] = new Set(chosen);
    }
    setAssignments(next);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // 1) Save scope
      const scopePayload = { departments: Array.from(selectedDeptIds), fdrs: [] };
      await fetch(`${API_BASE}/risk-assessment/assessments/${assessmentId}/scope`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scopePayload)
      });

      // 2) Save assignments
      const assignmentsPayload = {
        assignments: Array.from(selectedDeptIds).map(deptId => ({
          department_id: deptId,
          survey_id: deptSurvey[deptId] || null,
          due_at: deptDueAt[deptId] || null,
          user_ids: Array.from(assignments[deptId] || [])
        }))
      };
      const res = await fetch(`${API_BASE}/risk-assessment/assessments/${assessmentId}/assignments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(assignmentsPayload)
      });
      const json = await res.json();
      if (json.success) {
        onSaved?.();
        onClose?.();
      }
    } catch (_) {
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-slate-900/70" onClick={onClose} />
      <div className="relative bg-white w-full max-w-5xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2 text-sm">
              <span className={`px-2 py-1 rounded-full ${step === 1 ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'}`}>1</span>
              <span className="text-slate-700">Select Departments</span>
            </div>
            <div className="text-slate-300">→</div>
            <div className="flex items-center space-x-2 text-sm">
              <span className={`px-2 py-1 rounded-full ${step === 2 ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'}`}>2</span>
              <span className="text-slate-700">Choose Respondents</span>
            </div>
            <div className="text-slate-300">→</div>
            <div className="flex items-center space-x-2 text-sm">
              <span className={`px-2 py-1 rounded-full ${step === 3 ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'}`}>3</span>
              <span className="text-slate-700">Review & Save</span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-auto">
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">Select Departments in Scope</h3>
                <input
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  placeholder="Search departments..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {loading ? (
                  <div className="text-slate-500">Loading departments...</div>
                ) : (
                  filteredDepartments.map((dept) => (
                    <label key={dept.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-xl hover:bg-slate-50">
                      <div>
                        <div className="font-medium text-slate-900">{dept.name}</div>
                        {dept.aliases?.length ? (
                          <div className="text-xs text-slate-500">Aliases: {dept.aliases.join(', ')}</div>
                        ) : null}
                      </div>
                      <input
                        type="checkbox"
                        checked={selectedDeptIds.has(dept.id)}
                        onChange={() => toggleDept(dept.id)}
                      />
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">Choose Respondents</h3>
                <button
                  onClick={handleAutoSuggest}
                  className="px-3 py-2 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600"
                >
                  Auto-assign suggestions
                </button>
              </div>

              <div className="space-y-3">
                {Array.from(selectedDeptIds).map((deptId) => (
                  <div key={deptId} className="border border-slate-200 rounded-xl">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-t-xl">
                      <div className="font-medium text-slate-900">Department</div>
                      <button
                        onClick={() => ensureDeptUsersLoaded(deptId)}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        {deptUsers[deptId] ? 'Refresh' : 'Load users'}
                      </button>
                    </div>
                    <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      <div className="md:col-span-2 lg:col-span-3 flex items-center gap-3 mb-2">
                        <label className="text-sm text-slate-600">Survey template</label>
                        <select
                          className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                          value={deptSurvey[deptId] || ''}
                          onChange={(e) => setDeptSurvey(prev => ({ ...prev, [deptId]: e.target.value || null }))}
                        >
                          <option value="">Default</option>
                          {surveys.map(s => (
                            <option key={s.id} value={s.id}>{s.name}{s.is_default ? ' (Default)' : ''}</option>
                          ))}
                        </select>
                        <label className="text-sm text-slate-600 ml-4">Due date</label>
                        <input
                          type="date"
                          value={(deptDueAt[deptId] || '').slice(0,10)}
                          onChange={(e) => setDeptDueAt(prev => ({ ...prev, [deptId]: e.target.value ? new Date(e.target.value).toISOString() : null }))}
                          className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                        />
                      </div>
                      {(deptUsers[deptId] || []).map((u) => {
                        const checked = (assignments[deptId] || new Set()).has(u.id);
                        const roleBadge = u.app_role === 'lead' ? 'bg-blue-100 text-blue-700' : u.app_role === 'supervisor' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700';
                        const roleLabel = u.app_role || u.core_role;
                        return (
                          <label key={u.id} className={`flex items-center justify-between p-2 border rounded-lg ${checked ? 'border-blue-300 bg-blue-50' : 'border-slate-200'}`}>
                            <div>
                              <div className="font-medium text-slate-900">{u.name}</div>
                              <div className="text-xs text-slate-500">{u.email}</div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`text-xs px-2 py-0.5 rounded ${roleBadge}`}>{roleLabel}</span>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleAssignment(deptId, u.id)}
                              />
                            </div>
                          </label>
                        );
                      })}
                      {(!deptUsers[deptId] || deptUsers[deptId].length === 0) && (
                        <div className="text-sm text-slate-500">No users yet. Click "Load users" to fetch candidates.</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900">Review & Save</h3>
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="grid grid-cols-3 bg-slate-50 text-sm font-medium text-slate-600">
                  <div className="px-3 py-2">Department</div>
                  <div className="px-3 py-2">Respondents</div>
                  <div className="px-3 py-2">Count</div>
                </div>
                {Array.from(selectedDeptIds).map((deptId) => {
                  const users = deptUsers[deptId] || [];
                  const selectedUsers = users.filter(u => (assignments[deptId] || new Set()).has(u.id));
                  return (
                    <div key={deptId} className="grid grid-cols-3 text-sm border-t border-slate-200">
                      <div className="px-3 py-2">{departments.find(d => d.id === deptId)?.name || '—'}</div>
                      <div className="px-3 py-2 truncate">{selectedUsers.map(u => u.name).join(', ') || '—'}</div>
                      <div className="px-3 py-2">{selectedUsers.length}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-sm text-slate-500">{loading ? 'Saving...' : ''}</div>
          <div className="space-x-2">
            <button onClick={onClose} className="px-4 py-2 text-slate-700 border border-slate-300 rounded-lg hover:bg-white">Cancel</button>
            {step > 1 && (
              <button onClick={() => setStep(s => s - 1)} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-white">Back</button>
            )}
            {step < 3 && (
              <button
                onClick={() => setStep(s => s + 1)}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                disabled={step === 1 && selectedDeptIds.size === 0}
              >
                Next
              </button>
            )}
            {step === 3 && (
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                Save
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


